(() => {
  'use strict';
  // Locale strings from layout/theme.liquid (window.abrReviewsStrings); English when missing.
  const abrText = (key, english, values) => {
    const strings = window.abrReviewsStrings || {};
    let text = typeof strings[key] === 'string' && strings[key] ? strings[key] : english;
    if (values) Object.keys(values).forEach((name) => { text = text.split(`[${name}]`).join(String(values[name])); });
    return text;
  };

  if (window.__adobeNativeReviews) return;
  window.__adobeNativeReviews = true;

  const Data = () => window.AdobeReviewsData;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = new WeakMap();
  let scanFrame = 0;

  function stateFor(widget) {
    if (!states.has(widget)) {
      states.set(widget, {
        root: null,
        observer: null,
        scoreObserver: null,
        anchorFrame: 0,
        filterActive: false,
        reviews: null,
        releaseTimer: 0,
        loadingTimer: 0,
        selectedRating: null,
        scrollAfterPage: false,
        scrollFrame: 0,
        scrollCancel: null,
        pageHeightRestore: null,
        pageSpacer: null,
        settleTimer: 0
      });
    }
    return states.get(widget);
  }

  function disableJudgeMeAutoScroll(widget, root) {
    const targets = [widget, root].filter(Boolean);
    targets.forEach((target) => target.setAttribute('data-not-scroll-to', 'true'));

    const judgeQuery = window.jdgm?.$;
    if (typeof judgeQuery !== 'function') return;

    targets.forEach((target) => {
      try {
        judgeQuery(target).data('not-scroll-to', true);
      } catch (_) {
        return;
      }
    });
  }

  function installJudgeMeScrollGuard() {
    const judgeMe = window.jdgm;
    if (!judgeMe || typeof judgeMe.scrollTo !== 'function' || judgeMe.scrollTo.abrGuarded === true) return;

    const originalScrollTo = judgeMe.scrollTo;
    const guardedScrollTo = function(target, ...args) {
      const targetElement = target instanceof Element ? target : target?.[0];
      const widget = targetElement?.closest?.('.jdgm-review-widget');

      if (widget && stateFor(widget).filterActive) return;
      return originalScrollTo.call(this, target, ...args);
    };

    guardedScrollTo.abrGuarded = true;
    judgeMe.scrollTo = guardedScrollTo;
  }

  function syncClearFilter(root, selectedRating) {
    const clearFilter = root?.querySelector('.jdgm-histogram__clear-filter');
    if (!clearFilter) return;

    const isVisible = Boolean(selectedRating);
    clearFilter.classList.toggle('abr-clear-filter--visible', isVisible);
    clearFilter.hidden = !isVisible;
  }

  function buildOverview(widget, root) {
    const header = root.querySelector('.jdgm-rev-widg__header');
    const summary = root.querySelector('.jdgm-rev-widg__summary');
    const histogram = root.querySelector('.jdgm-histogram');
    const write = root.querySelector('.jdgm-write-rev-link');
    if (!header || !summary || !histogram) return;

    let overview = header.querySelector(':scope > .abr-overview');
    if (!overview) {
      overview = document.createElement('div');
      overview.className = 'abr-overview';
      const title = header.querySelector('.jdgm-rev-widg__title');
      title?.insertAdjacentElement('afterend', overview);
    }

    summary.classList.add('abr-summary');
    histogram.classList.add('abr-distribution');

    const overviewOrderIsCorrect =
      summary.parentElement === overview &&
      histogram.parentElement === overview &&
      overview.children[0] === summary &&
      overview.children[1] === histogram;

    if (!overviewOrderIsCorrect) {
      overview.insertBefore(summary, overview.firstElementChild);
      summary.insertAdjacentElement('afterend', histogram);
    }

    if (write) {
      let actions = overview.querySelector('.abr-overview__actions');
      if (!actions) {
        actions = document.createElement('div');
        actions.className = 'abr-overview__actions';
        overview.append(actions);
      }
      if (!actions.contains(write)) actions.append(write);
    }

    let score = summary.querySelector('.abr-score');
    if (!score) {
      score = document.createElement('span');
      score.className = 'abr-score';
      score.setAttribute('aria-hidden', 'true');
      summary.prepend(score);
    }

    const value = Data().rating(widget);
    score.dataset.value = value.toFixed(2).replace(/0$/, '');
    if (score.textContent !== score.dataset.value) score.textContent = score.dataset.value;
    if (widget.dataset.abrScoreAnimated !== 'true') revealScore(score, widget);

    const selectedRating = stateFor(widget).selectedRating;
    Data().distribution(widget).forEach(({ row, rating, percentage }) => {
      row.classList.add('abr-rating-row');
      row.classList.toggle('abr-rating-row--selected', String(rating) === selectedRating);
      row.setAttribute('aria-pressed', String(String(rating) === selectedRating));
      row.style.setProperty('--abr-rating-width', Math.max(0, Math.min(100, percentage)) + '%');
      const star = row.querySelector('.jdgm-histogram__star');
      if (star) star.dataset.abrLabel = rating + ' ★';
    });

    syncClearFilter(root, selectedRating);
  }

  // The score shows its real value from the start and fades in once it
  // scrolls into view: a soft rise with a light blur that clears, on the
  // same ease-out as the rest of the section's entrances.
  function revealScore(score, widget) {
    const state = stateFor(widget);
    const finish = () => {
      state.scoreObserver?.disconnect();
      state.scoreObserver = null;
      score.style.removeProperty('opacity');
      widget.dataset.abrScoreAnimated = 'true';
    };

    if (reducedMotion.matches || !('IntersectionObserver' in window) || typeof score.animate !== 'function') {
      finish();
      return;
    }

    if (state.scoreObserver) return;

    // Hidden until in view, so it never shows and then fades.
    score.style.opacity = '0';

    state.scoreObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      finish();
      score.animate([
        { opacity: 0, transform: 'translateY(8px)', filter: 'blur(6px)' },
        { opacity: 1, transform: 'none', filter: 'blur(0)' }
      ], {
        duration: 900,
        delay: 150,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'backwards'
      });
    }, { threshold: 0.4 });

    state.scoreObserver.observe(score);
  }

  function hasMeaningfulReviewText(value) {
    return String(value || '').replace(/[\s\u200B-\u200F\u2060\uFEFF]/g, '').length > 0;
  }

  // Judge.me timestamps look like "2024-03-01 12:00:00 UTC".
  function parseReviewTime(value) {
    const raw = String(value || '').trim();
    if (!raw) return 0;
    return Date.parse(raw) || Date.parse(raw.replace(' UTC', 'Z').replace(' ', 'T')) || 0;
  }

  let relativeReviewTimeFormatter;
  function relativeReviewTime(time) {
    if (!time) return '';
    const days = Math.round((Date.now() - time) / 86400000);
    const format = relativeReviewTimeFormatter ||= new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    if (days >= 365) return format.format(-Math.round(days / 365), 'year');
    if (days >= 30) return format.format(-Math.round(days / 30), 'month');
    if (days >= 7) return format.format(-Math.round(days / 7), 'week');
    return format.format(-Math.max(0, days), 'day');
  }

  /*
   * Finish a review ourselves instead of relying on Judge.me's script to
   * have done it: on a slow connection the reviews were shown (and used
   * as the template for the other pages) while still raw - no rating /
   * profile rows, the stock avatar glyph, a spinning timestamp. Idempotent,
   * and it runs before the section is shown, so it only ever appears
   * finished; if Judge.me finishes a review later, nothing is doubled.
   */
  function normalizeReview(review) {
    const header = review.querySelector('.jdgm-rev__header');
    if (!header) return;
    const find = (selector) => header.querySelector(selector);
    const rating = find('.jdgm-rev__rating');
    const stamp = find('.jdgm-rev__timestamp');
    const icon = find('.jdgm-rev__icon');
    const author = find('.jdgm-rev__author-wrapper');
    const badge = find('.jdgm-rev__buyer-badge-wrapper');

    if (!find('.jdgm-row-rating') && (rating || stamp)) {
      const row = document.createElement('div');
      row.className = 'jdgm-row-rating';
      [rating, stamp].forEach((element) => element && row.append(element));
      header.prepend(row);
    }
    if (!find('.jdgm-row-profile') && (icon || author)) {
      const row = document.createElement('div');
      row.className = 'jdgm-row-profile';
      [icon, author].forEach((element) => element && row.append(element));
      header.append(row);
    }
    if (badge && author && !author.contains(badge)) author.append(badge);
    // Judge.me's raw line break would take a cell in the header grid.
    header.querySelectorAll(':scope > .jdgm-rev__br').forEach((element) => element.remove());

    // Stars from the score (Judge.me draws them in its script) - exactly
    // five, even if it draws its own later.
    if (rating) {
      const stars = rating.querySelectorAll('.jdgm-star');
      if (!stars.length) {
        const score = Number.parseFloat(rating.dataset.score) || 0;
        for (let i = 1; i <= 5; i += 1) {
          const star = document.createElement('span');
          star.className = `jdgm-star ${score >= i ? 'jdgm--on' : score >= i - 0.5 ? 'jdgm--half' : 'jdgm--off'}`;
          rating.append(star);
        }
      } else if (stars.length > 5) {
        Array.from(stars).slice(0, stars.length - 5).forEach((star) => star.remove());
      }
    }

    // The date instead of Judge.me's spinning placeholder.
    review.querySelectorAll('.jdgm-spinner').forEach((element) => element.classList.remove('jdgm-spinner'));
    if (stamp && !stamp.textContent.trim()) {
      const text = relativeReviewTime(parseReviewTime(stamp.dataset.content));
      if (text) stamp.textContent = text;
    }

    // Empty store-reply boxes never show.
    review.querySelectorAll('.jdgm-rev__reply').forEach((reply) => {
      if (!reply.textContent.replace(/[\s\u200B-\u200F\u2060\uFEFF]/g, '') && !reply.querySelector('img, video, iframe')) reply.remove();
    });
  }

  // A reviewer's photo loads in its tile and fades in once it can be drawn.
  // Judge.me marks a photo that is still loading with jdgm--loading (its
  // stylesheet then hides the photo behind a light frame and a spinning
  // outline), and only its own script takes the class off - never on the
  // copies the review list is built from, so the spinner stayed over a photo
  // that had arrived. The class goes; data-abr-photo holds the photo back
  // (adobe-reviews-alignment-fix-v2.css) until it has loaded, or for good if
  // it can't load. While it loads near the screen ("loading") the tile's
  // skeleton breathes and shimmers like the store's other pictures; further
  // away ("waiting") it holds still, as a loop running off screen would
  // still restyle the page every frame.
  const watchedPhotos = new WeakSet();
  const photosNearScreen = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const link = entry.target;
        if (link.dataset.abrPhoto === 'loading' || link.dataset.abrPhoto === 'waiting') {
          link.dataset.abrPhoto = entry.isIntersecting ? 'loading' : 'waiting';
        }
      });
    }, { rootMargin: '200px 0px' })
    : null;

  function watchPhoto(image) {
    const link = image.closest('.jdgm-rev__pic-link');
    if (!link) return;
    if (link.classList.contains('jdgm--loading')) link.classList.remove('jdgm--loading');
    if (watchedPhotos.has(image)) return;
    watchedPhotos.add(image);

    const settle = (state) => {
      photosNearScreen?.unobserve(link);
      if (state) link.dataset.abrPhoto = state;
      else delete link.dataset.abrPhoto;
    };
    if (image.complete) {
      settle(image.naturalWidth ? null : 'error');
      return;
    }
    if (photosNearScreen) {
      link.dataset.abrPhoto = 'waiting';
      photosNearScreen.observe(link);
    } else {
      link.dataset.abrPhoto = 'loading';
    }
    image.addEventListener('load', () => {
      const shown = () => settle(null);
      if (typeof image.decode === 'function') image.decode().then(shown, shown);
      else shown();
    }, { once: true });
    image.addEventListener('error', () => settle('error'), { once: true });
  }

  function decorateReviews(root) {
    const list = root.querySelector('.jdgm-rev-widg__reviews');
    if (list && !root.querySelector('.abr-review-list-note')) {
      const note = document.createElement('p');
      note.className = 'abr-review-list-note';
      note.textContent = abrText('list_note', 'Reviews with only a star rating are hidden from this list.');
      list.insertAdjacentElement('beforebegin', note);
    }

    root.querySelectorAll('.jdgm-rev').forEach((review) => {
      review.classList.add('abr-review');
      normalizeReview(review);

      const verified = review.dataset.verifiedBuyer === 'true';
      review.classList.toggle('abr-review--verified', verified);

      const title = review.querySelector('.jdgm-rev__title');
      const body = review.querySelector('.jdgm-rev__body');
      const author = review.querySelector('.jdgm-rev__author');
      const icon = review.querySelector('.jdgm-rev__icon');
      const rating = review.querySelector('.jdgm-rev__rating');

      if (author && icon) {
        const nameParts = author.textContent.trim().split(/\s+/).filter(Boolean);
        icon.dataset.abrInitials = nameParts.slice(0, 2).map((part) => Array.from(part)[0]).join('').toUpperCase();
        icon.setAttribute('aria-hidden', 'true');
      }

      if (rating) {
        const score = Number.parseFloat(rating.dataset.score || rating.getAttribute('aria-label'));
        if (Number.isFinite(score)) rating.dataset.abrRating = score.toFixed(1);
      }

      body?.classList.add('abr-review__body');

      const pics = review.querySelector('.jdgm-rev__pics');
      const vids = review.querySelector('.jdgm-rev__vids');
      // A review with the reviewer's photos (or video) is shown even without
      // words; only a bare star rating is hidden.
      const hasMedia = Boolean(pics?.querySelector('img') || vids?.querySelector('video, iframe'));

      const generatedEmpty = body?.querySelector('.abr-review__empty');
      const hasTitle = hasMeaningfulReviewText(title?.textContent);
      const hasBody = !generatedEmpty && hasMeaningfulReviewText(body?.textContent);
      const isRatingOnly = !hasTitle && !hasBody && !hasMedia;

      generatedEmpty?.remove();
      review.classList.toggle('abr-review--rating-only', isRatingOnly);
      review.classList.toggle('abr-review--photos-only', !hasTitle && !hasBody && hasMedia);
      review.hidden = isRatingOnly;
      if (isRatingOnly) review.setAttribute('aria-hidden', 'true');
      else review.removeAttribute('aria-hidden');

      pics?.querySelectorAll('img').forEach((image) => {
        image.loading = 'lazy';
        // an address Judge.me's script has not loaded yet (data-src)
        const current = image.getAttribute('src');
        if (image.dataset.src && (!current || current.startsWith('data:'))) image.src = image.dataset.src;
        watchPhoto(image);
      });
      vids?.querySelectorAll('video, iframe').forEach((media) => media.loading = 'lazy');
    });

    const note = root.querySelector('.abr-review-list-note');
    if (note && list) {
      const hasVisibleReviews = Boolean(list.querySelector('.jdgm-rev:not([hidden])'));
      if (note.hidden === hasVisibleReviews) note.hidden = !hasVisibleReviews;
      const display = hasVisibleReviews ? '' : 'none';
      if (note.style.display !== display) note.style.display = display;
    }
  }

  function decorateSort(root) {
    root.querySelectorAll('.jdgm-sort-dropdown[name="reviewer_name_format"]').forEach((nameFormat) => {
      nameFormat.classList.remove('abr-source-select');
      Array.from(nameFormat.options || []).forEach((option) => {
        option.hidden = false;
        option.disabled = false;
      });
    });

    const select =
      root.querySelector('.jdgm-rev-widg__sort-wrapper .jdgm-sort-dropdown') ||
      root.querySelector('.jdgm-sort-dropdown:not([name="reviewer_name_format"])');
    if (!select) return;

    const allowedLabels = new Set(['most recent', 'highest rating', 'lowest rating']);
    const options = Array.from(select.options || []);
    const allowedOptions = options.filter((option) =>
      allowedLabels.has(option.textContent.trim().toLowerCase())
    );

    options.forEach((option) => {
      const allowed = allowedOptions.includes(option);
      option.hidden = !allowed;
      option.disabled = !allowed;
    });

    if (select.closest('.abr-sort')) return;

    const wrapper = select.closest('.jdgm-rev-widg__sort-wrapper') || select.parentElement;
    if (!wrapper || wrapper.querySelector('.abr-sort')) return;

    const details = document.createElement('details');
    details.className = 'abr-sort';

    const summary = document.createElement('summary');
    summary.className = 'abr-sort__trigger';

    const label = document.createElement('span');
    label.textContent = select.selectedOptions?.[0]?.textContent?.trim() || select.value;
    summary.append(label);

    const menu = document.createElement('div');
    menu.className = 'abr-sort__menu';

    allowedOptions.forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'abr-sort__option';
      button.dataset.value = option.value;
      button.textContent = option.textContent.trim();
      button.setAttribute('aria-pressed', String(option.selected));

      button.addEventListener('click', () => {
        if (select.value !== option.value) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        closeSort(details);
        summary.focus();
      });

      menu.append(button);
    });

    select.classList.add('abr-source-select');
    details.append(summary, menu);
    wrapper.append(details);

    // Closing plays the menu's exit before <details> actually closes
    // (it would otherwise vanish at once); outside clicks and Escape
    // close it too.
    summary.addEventListener('click', (event) => {
      if (!details.open) return;
      event.preventDefault();
      closeSort(details);
    });
    document.addEventListener('click', (event) => {
      if (details.open && !details.contains(event.target)) closeSort(details);
    });
    details.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && details.open) {
        closeSort(details);
        summary.focus();
      }
    });

    select.addEventListener('change', () => {
      label.textContent = select.selectedOptions?.[0]?.textContent?.trim() || select.value;
      menu.querySelectorAll('.abr-sort__option').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.value === select.value));
      });
    });
  }

  function closeSort(details) {
    if (!details.open || details.classList.contains('abr-sort--closing')) return;
    if (reducedMotion.matches) {
      details.open = false;
      return;
    }
    details.classList.add('abr-sort--closing');
    const menu = details.querySelector('.abr-sort__menu');
    const finish = () => {
      window.clearTimeout(timer);
      menu?.removeEventListener('animationend', onEnd);
      details.classList.remove('abr-sort--closing');
      details.open = false;
    };
    const onEnd = (event) => {
      if (event.target === menu) finish();
    };
    const timer = window.setTimeout(finish, 260);
    menu?.addEventListener('animationend', onEnd);
  }

  function decoratePagination(root) {
    root.querySelectorAll('.jdgm-paginate__page').forEach((page) => {
      page.classList.add('abr-page');
    });
  }

  // The whole card rises in as it scrolls into view, with the histogram
  // bars growing in alongside. Once per widget: a root Judge.me renders
  // again later (sorting, paging) is simply there.
  function revealOnScroll(widget, root) {
    const state = stateFor(widget);
    const ready = () => {
      if (root.dataset.abrReady !== 'true') root.dataset.abrReady = 'true';
    };
    if (state.revealed || reducedMotion.matches || !('IntersectionObserver' in window)) {
      root.classList.remove('abr-reveal-pending');
      requestAnimationFrame(ready);
      return;
    }
    if (state.revealRoot === root) return;
    state.revealObserver?.disconnect();
    state.revealRoot = root;
    root.classList.add('abr-reveal-pending');
    state.revealObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      state.revealObserver.disconnect();
      state.revealObserver = null;
      state.revealed = true;
      requestAnimationFrame(() => {
        root.classList.add('abr-revealing');
        root.classList.remove('abr-reveal-pending');
        ready();
        window.setTimeout(() => root.classList.remove('abr-revealing'), 1400);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    state.revealObserver.observe(root);
  }

  function enhance(widget) {
    if (!Data()) return;

    const root = Data().liveRoot(widget);
    if (!root) return;

    const state = stateFor(widget);
    root.classList.add('abr-reviews');
    installJudgeMeScrollGuard();
    disableJudgeMeAutoScroll(widget, root);

    buildOverview(widget, root);

    revealOnScroll(widget, root);
    decorateReviews(root);
    decorateSort(root);
    decoratePagination(root);

    const reviews = root.querySelector('.jdgm-rev-widg__reviews');
    if (state.root === root && state.reviews === reviews && state.observer) return;

    state.observer?.disconnect();
    const previousReviews = state.reviews;
    state.root = root;
    state.reviews = reviews;

    state.observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (record.type === 'attributes') {
          return record.target instanceof Element &&
            record.target.matches('.jdgm-review-widget, .jdgm-rev-widg, .jdgm-legacy-widget-content');
        }

        return record.addedNodes.length || record.removedNodes.length;
      });
      if (!relevant) return;
      // Style (and hide the star-only) reviews Judge.me just inserted right
      // away - this runs before the next paint - instead of on the next
      // scan: that gap is what flashed its own gray/blue-bordered rows.
      const liveRoot = Data()?.liveRoot(widget);
      if (liveRoot) decorateReviews(liveRoot);
      queueScan();
      // Only content actually arriving ends a page change - not style
      // attribute writes (beginChange's own among them), which ended it
      // before the new page was even there.
      if (records.some((record) => record.type === 'childList' && (record.addedNodes.length || record.removedNodes.length))) {
        scheduleFinishChange(widget);
      }
    });

    state.observer.observe(widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });

    if (previousReviews && previousReviews !== reviews) finishChange(widget);
  }

  function directionFor(control, widget) {
    if (control.matches('.jdgm-paginate__next-page, [rel="next"]')) return 1;
    if (control.matches('.jdgm-paginate__prev-page, [rel="prev"]')) return -1;

    const from = Data().currentPage(widget);
    const to = Number.parseInt(control.dataset.page || control.textContent, 10);
    if (!Number.isFinite(to) || to === from) return 0;
    return to > from ? 1 : -1;
  }

  function beginChange(widget, direction, root = Data().liveRoot(widget)) {
    const state = stateFor(widget);
    const reviews = root?.querySelector('.jdgm-rev-widg__reviews');
    const body = root?.querySelector('.jdgm-rev-widg__body');
    if (!root || !reviews) return;

    // Measured before the html class below (it only switches overflow-anchor
    // off), so this read lays out the caller's pending writes with scroll
    // anchoring still on, exactly like the liveRoot() read that used to sit
    // here, and the class costs no separate forced style pass of its own.
    const height = Math.ceil(reviews.getBoundingClientRect().height);

    window.clearTimeout(state.releaseTimer);
    window.clearTimeout(state.loadingTimer);
    window.clearTimeout(state.settleTimer);
    window.cancelAnimationFrame(state.anchorFrame);
    document.documentElement.classList.add('abr-review-update-active');
    state.filterActive = true;

    if (height) root.style.setProperty('--abr-review-list-height', height + 'px');

    if (body) {
      const bodyHeight = Math.ceil(body.getBoundingClientRect().height);
      // Read once: the live declaration used to be re-read after each custom
      // property write below, forcing a style pass of the widget per read.
      // Those writes can't change these values (while loading, the body's
      // paddings/margins come from these very properties, set to the values
      // just read).
      const { paddingTop, paddingBottom, marginTop, marginBottom } = window.getComputedStyle(body);
      if (bodyHeight) root.style.setProperty('--abr-review-body-height', bodyHeight + 'px');
      root.style.setProperty('--abr-review-body-padding-top', paddingTop);
      root.style.setProperty('--abr-review-body-padding-bottom', paddingBottom);
      root.style.setProperty('--abr-review-body-margin-top', marginTop);
      root.style.setProperty('--abr-review-body-margin-bottom', marginBottom);
    }

    root.style.setProperty('--abr-leave-x', direction < 0 ? '8px' : '-8px');
    root.style.setProperty('--abr-enter-x', direction < 0 ? '-8px' : '8px');
    root.classList.remove('abr-is-entering', 'abr-is-leaving');
    root.classList.add('abr-is-loading');
    root.setAttribute('aria-busy', 'true');

    state.releaseTimer = window.setTimeout(() => finishChange(widget), 8000);
  }

  function scheduleFinishChange(widget) {
    const state = stateFor(widget);
    window.clearTimeout(state.settleTimer);
    // Judge.me inserts a page in one quick burst of mutations; a short
    // settle is enough (at 700ms the new page sat dimmed long after it
    // had arrived).
    state.settleTimer = window.setTimeout(() => finishChange(widget), 140);
  }

  function reviewScrollTop(root) {
    if (!root.isConnected) return;
    // The overview uses display:contents and has no bounding box. Scroll
    // to its rendered rating panels, never to that structural wrapper.
    const desktopTarget = window.matchMedia('(min-width: 700px)').matches
      ? Array.from(root.querySelectorAll('.jdgm-rev-widg__summary, .jdgm-histogram'))
        .filter(panel => panel.getClientRects().length)
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)[0]
      : null;
    const target = desktopTarget || Array.from(root.querySelectorAll('.jdgm-rev-widg__sort-wrapper, .jdgm-sort-dropdown-wrapper:not(.jdgm-rev-widg__sort-wrapper *)')).find(row => row.getClientRects().length) || root.querySelector('.jdgm-rev-widg__reviews') || root;
    if (!target) return;
    const header = document.querySelector('header');
    const bounds = header?.getBoundingClientRect();
    const offset = bounds && !window.matchMedia('(min-width: 700px)').matches
      ? Math.min(bounds.height, 160)
      : bounds && bounds.top >= 0 && bounds.top < 100 ? Math.min(bounds.bottom, 160) : 64;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset - 12);
    return top;
  }

  function scrollToReviewSummary(root) {
    const top = reviewScrollTop(root);
    if (top !== undefined) window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  // Preserve the outgoing page's document height before replacing its cards.
  // Otherwise a short last page clamps the browser's scroll position before
  // the upward scroll can start, particularly near the bottom on phones.
  function holdReviewPageHeight(widget) {
    const state = stateFor(widget);
    const root = Data()?.liveRoot(widget);
    if (!root) return;
    window.cancelAnimationFrame(state.scrollFrame);
    state.scrollCancel?.();
    if (!state.pageHeightRestore) {
      const value = root.style.getPropertyValue('min-height');
      const priority = root.style.getPropertyPriority('min-height');
      state.pageHeightRestore = () => {
        if (value) root.style.setProperty('min-height', value, priority);
        else root.style.removeProperty('min-height');
        state.pageHeightRestore = null;
      };
    }
    const height = root.getBoundingClientRect().height + (state.pageSpacer?.getBoundingClientRect().height || 0);
    root.style.setProperty('min-height', `${height}px`, 'important');
    state.pageSpacer?.remove();
    state.pageSpacer = null;
  }

  function releaseReviewPageHeight(widget) {
    const state = stateFor(widget);
    state.scrollCancel?.();
    state.scrollCancel = null;
    state.pageHeightRestore?.();
    state.pageSpacer?.remove();
    state.pageSpacer = null;
  }

  function queueReviewScroll(widget) {
    const state = stateFor(widget);
    window.cancelAnimationFrame(state.scrollFrame);
    state.scrollCancel?.();
    const root = Data()?.liveRoot(widget);
    if (root && state.pageHeightRestore) {
      // Keep document length, not the card's painted gray background. Add
      // the spacer before unpinning so the browser cannot clamp scrollY.
      const heldHeight = root.getBoundingClientRect().height;
      const spacer = document.createElement('div');
      spacer.setAttribute('aria-hidden', 'true');
      spacer.style.cssText = `height:${heldHeight}px;pointer-events:none;overflow-anchor:none;`;
      root.after(spacer);
      state.pageSpacer = spacer;
      state.pageHeightRestore();
      spacer.style.height = `${Math.max(0, heldHeight - root.getBoundingClientRect().height)}px`;
    }
    state.scrollFrame = window.requestAnimationFrame(() => {
      state.scrollFrame = window.requestAnimationFrame(() => {
        state.scrollFrame = 0;
        const root = Data()?.liveRoot(widget);
        if (!root) {
          releaseReviewPageHeight(widget);
          return;
        }
        const from = window.scrollY;
        const destination = reviewScrollTop(root);
        if (destination === undefined) {
          releaseReviewPageHeight(widget);
          return;
        }
        const duration = reducedMotion.matches ? 0 : Math.min(650, Math.max(340, Math.abs(destination - from) * 0.18));
        const started = performance.now();
        const interruptions = ['wheel', 'touchstart', 'keydown'];
        const interrupt = () => {
          window.cancelAnimationFrame(state.scrollFrame);
          state.scrollFrame = 0;
          releaseReviewPageHeight(widget);
        };
        state.scrollCancel = () => {
          interruptions.forEach(type => window.removeEventListener(type, interrupt));
          state.scrollCancel = null;
        };
        interruptions.forEach(type => window.addEventListener(type, interrupt, { passive: true }));
        const frame = (now) => {
          if (!root.isConnected) return interrupt();
          const progress = duration ? Math.min(1, (now - started) / duration) : 1;
          const top = reviewScrollTop(root);
          const eased = 1 - Math.pow(1 - progress, 3);
          // Own this scroll so a document resize cannot cancel a native
          // smooth scroll midway through. Re-measure in case content above loads.
          window.scrollTo({ top: from + (top - from) * eased, behavior: 'instant' });
          const extra = state.pageSpacer?.getBoundingClientRect().height || 0;
          const naturalMax = Math.max(0, document.documentElement.scrollHeight - extra - window.innerHeight);
          if (extra && window.scrollY <= naturalMax + 0.5) {
            state.pageSpacer.remove();
            state.pageSpacer = null;
          }
          if (progress < 1) state.scrollFrame = window.requestAnimationFrame(frame);
          else {
            state.scrollFrame = 0;
            releaseReviewPageHeight(widget);
          }
        };
        frame(started);

      });
    });
  }

  function finishChange(widget) {
    const state = stateFor(widget);
    const root = Data()?.liveRoot(widget);
    const reviews = root?.querySelector('.jdgm-rev-widg__reviews');
    if (!root || !reviews) return;
    // Once per change: a late settle timer after the page is already in
    // would replay the fade.
    if (!root.classList.contains('abr-is-loading')) return;

    window.clearTimeout(state.loadingTimer);
    window.clearTimeout(state.releaseTimer);
    window.clearTimeout(state.settleTimer);
    root.classList.remove('abr-is-leaving');

    if (!reducedMotion.matches) {
      root.classList.remove('abr-is-entering');
      void reviews.offsetWidth;
      root.classList.add('abr-is-entering');
    }

    // The list held the previous page's height while loading; the new
    // page is often shorter (star-only reviews are hidden), which left a
    // blank band for over a second. Let go of it now and ease the body
    // from the old height to the new one instead.
    const release = () => {
      const body = root.querySelector('.jdgm-rev-widg__body');
      const from = body ? body.getBoundingClientRect().height : 0;
      body?.style.removeProperty('display');
      body?.style.removeProperty('height');
      body?.style.removeProperty('overflow');
      body?.style.removeProperty('padding-top');
      body?.style.removeProperty('padding-bottom');
      body?.style.removeProperty('margin-top');
      body?.style.removeProperty('margin-bottom');
      root.classList.remove('abr-is-loading');
      window.setTimeout(() => root.classList.remove('abr-is-entering'), reducedMotion.matches ? 0 : 420);
      root.removeAttribute('aria-busy');
      root.style.removeProperty('--abr-review-list-height');
      root.style.removeProperty('--abr-review-body-height');
      root.style.removeProperty('--abr-review-body-padding-top');
      root.style.removeProperty('--abr-review-body-padding-bottom');
      root.style.removeProperty('--abr-review-body-margin-top');
      root.style.removeProperty('--abr-review-body-margin-bottom');

      // (An inline !important height: the stylesheet pins the body's
      // height with !important, which a WAAPI animation can't override.)
      const to = body ? body.getBoundingClientRect().height : 0;
      if (body && from && to && Math.abs(from - to) > 2 && !reducedMotion.matches && !state.pageHeightRestore && !state.pageSpacer) {
        body.style.setProperty('overflow', 'hidden', 'important');
        body.style.setProperty('height', `${from}px`, 'important');
        void body.offsetHeight;
        body.style.setProperty('transition', 'height 420ms cubic-bezier(0.32, 0.72, 0, 1)', 'important');
        body.style.setProperty('height', `${to}px`, 'important');
        const done = () => {
          window.clearTimeout(fallback);
          body.removeEventListener('transitionend', onEnd);
          ['height', 'overflow', 'transition'].forEach((property) => body.style.removeProperty(property));
        };
        const onEnd = (event) => {
          if (event.target === body && event.propertyName === 'height') done();
        };
        const fallback = window.setTimeout(done, 520);
        body.addEventListener('transitionend', onEnd);
      }

      state.anchorFrame = window.requestAnimationFrame(() => {
        state.anchorFrame = window.requestAnimationFrame(() => {
          document.documentElement.classList.remove('abr-review-update-active');
          state.filterActive = false;
          if (state.scrollAfterPage) {
            state.scrollAfterPage = false;
            scrollToReviewSummary(root);
          }
        });
      });
    };
    release();

  }

  function queueScan() {
    if (scanFrame) return;
    scanFrame = requestAnimationFrame(() => {
      scanFrame = 0;
      const widget = Data()?.widget();
      if (widget) enhance(widget);
    });
  }

  function cleanupWidget(widget) {
    const state = states.get(widget);
    if (!state) return;

    state.observer?.disconnect();
    state.scoreObserver?.disconnect();
    window.clearTimeout(state.releaseTimer);
    window.clearTimeout(state.loadingTimer);
    window.clearTimeout(state.settleTimer);
    window.cancelAnimationFrame(state.anchorFrame);
    window.cancelAnimationFrame(state.scrollFrame);
    releaseReviewPageHeight(widget);
    if (state.filterActive) document.documentElement.classList.remove('abr-review-update-active');
    states.delete(widget);
  }

  function cleanupWidgets(root) {
    if (!(root instanceof Element)) return;
    if (root.matches('.jdgm-review-widget')) cleanupWidget(root);
    root.querySelectorAll('.jdgm-review-widget').forEach(cleanupWidget);
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest('.jdgm-paginate__page, .jdgm-histogram__star, .jdgm-histogram__row[data-rating], .jdgm-histogram__clear-filter');
    if (!control) return;

    const widget = control.closest('.jdgm-review-widget');
    if (!widget) return;

    installJudgeMeScrollGuard();
    disableJudgeMeAutoScroll(widget, Data().liveRoot(widget));

    const row = control.closest('.jdgm-histogram__row') || (control.matches('.jdgm-histogram__clear-filter') ? control : null);
    if (row) {
      const clearFilter = row.classList.contains('jdgm-histogram__clear-filter');
      const state = stateFor(widget);
      state.scrollAfterPage = false;
      const root = Data().liveRoot(widget);
      const rating = String(row.dataset.rating || '');
      const isSelectedRating = !clearFilter && rating && state.selectedRating === rating;

      // Pressing the selected rating again clears the filter - exactly as
      // "See all reviews" does, by clicking it. (It used to be hidden
      // first and clicked after, which Judge.me could ignore; this click
      // runs back through here as a clear, which resets the rows.)
      if (isSelectedRating) {
        const clearControl = root?.querySelector('.jdgm-histogram__clear-filter');
        event.preventDefault();
        event.stopImmediatePropagation();
        if (clearControl instanceof HTMLElement) {
          clearControl.hidden = false;
          clearControl.click();
        } else {
          state.selectedRating = null;
          root?.querySelectorAll('.abr-rating-row').forEach((ratingRow) => {
            ratingRow.classList.remove('abr-rating-row--selected');
            ratingRow.setAttribute('aria-pressed', 'false');
          });
          syncClearFilter(root, null);
        }
        return;
      }

      state.selectedRating = clearFilter ? null : rating;
      root?.querySelectorAll('.abr-rating-row').forEach((ratingRow) => {
        const isSelected = !clearFilter && ratingRow.dataset.rating === state.selectedRating;
        ratingRow.classList.toggle('abr-rating-row--selected', isSelected);
        ratingRow.setAttribute('aria-pressed', String(isSelected));
      });
      syncClearFilter(root, state.selectedRating);
      // Same live root beginChange would look up again: the row writes above
      // only restyle the histogram, not the review roots' visibility.
      beginChange(widget, 0, root);
      return;
    }

    const direction = control.matches('.jdgm-paginate__page') ? directionFor(control, widget) : 0;
    stateFor(widget).scrollAfterPage = direction !== 0;
    beginChange(widget, direction);
  }, true);

  document.addEventListener('adobe-reviews:before-page', (event) => {
    const widget = event.target.closest?.('.jdgm-review-widget');
    if (widget) holdReviewPageHeight(widget);
  });

  // All-reviews paging (adobe-reviews-all-v1.js) says when its page is in.
  document.addEventListener('adobe-reviews:rendered', (event) => {
    const widget = event.target.closest?.('.jdgm-review-widget');
    if (!widget) return;
    if (event.detail?.pageChanged) stateFor(widget).scrollAfterPage = false;
    finishChange(widget);
    if (event.detail?.pageChanged) queueReviewScroll(widget);
    else releaseReviewPageHeight(widget);
  });

  document.addEventListener('change', (event) => {
    const control = event.target.closest('.jdgm-sort-dropdown, .jdgm-rev-widg__filter select');
    const widget = control?.closest('.jdgm-review-widget');
    if (widget) beginChange(widget, 0);
  }, true);

  const documentObserver = new MutationObserver((records) => {
    records.forEach((record) => record.removedNodes.forEach(cleanupWidgets));

    const relevant = records.some((record) => {
      if (
        record.type === 'attributes' &&
        record.target instanceof Element &&
        record.target.matches('.jdgm-review-widget, .jdgm-rev-widg, .jdgm-legacy-widget-content')
      ) {
        return true;
      }

      return Array.from(record.addedNodes).some((node) =>
        node instanceof Element && (
          node.matches('.jdgm-review-widget, .jdgm-rev-widg') ||
          node.querySelector('.jdgm-review-widget, .jdgm-rev-widg')
        )
      );
    });
    if (relevant) queueScan();
  });

  function start() {
    installJudgeMeScrollGuard();
    queueScan();

    [50, 150, 350, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(queueScan, delay);
    });

    documentObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', queueScan);
  document.addEventListener('shopify:section:unload', (event) => cleanupWidgets(event.target));
  document.addEventListener('adobe-reviews:data-ready', queueScan);
})();




